// CANONICAL COPY (MNSA-01, the GH #7115 pattern). src/selectors/assignments.js re-exports this file so the browser and the
// Function App share one definition. Edit here, never there.
'use strict';

/**
 * src/selectors/assignments.js
 *
 * Single source of truth for **dated, user-assigned work items** ("assignments")
 * derived from tenant state. An assignment is something a specific user owes by a
 * point in time — a document review, an open task, a risk action, a batch of
 * gap-assessment questions, etc.
 *
 * Why this module exists
 * ----------------------
 * The Compliance Calendar and the Action Centre both need to answer the same
 * question — "what does this user owe, and when?" — but historically each
 * computed its own list from raw state. Sources drifted: the calendar only ever
 * knew about documents, tasks and a synthetic risk-reassessment date, so whole
 * categories of assigned work (e.g. gap-analysis questions) never appeared on
 * any deadline surface even though a user had been explicitly assigned to them.
 *
 * This selector centralises the derivation behind one contributor registry so a
 * new assignable entity is surfaced everywhere at once, and the coverage gate
 * (`scripts/check-assignment-coverage.js`) fails the build if an assignment-
 * bearing collection is added without a contributor.
 *
 * Boundaries
 * ----------
 * - Pure: no I/O, no React, no mutation of `state`.
 * - `audience: 'admin-app'` on every assignment. The employee self-service
 *   portal is a separate surface (EMP-B-003) — its tasks are never aggregated
 *   here and must never be.
 * - A contributor only emits work that is genuinely *assigned* and *actionable*;
 *   it never invents a due date the source data does not carry (a missing
 *   `dueDate` stays `null` and date-only consumers skip it).
 */

const { normaliseMrAction, deriveVerificationState } = require('../managementReview/actions.js');

// ─── Normalised assignment shape ─────────────────────────────────────────────
/**
 * @typedef {Object} Assignment
 * @property {string}  id          Stable unique id (kind-scoped).
 * @property {string}  kind        Contributor kind, e.g. 'document-review'.
 * @property {('document'|'task'|'risk'|'audit')} module  Calendar bucket.
 * @property {string}  title       Primary label.
 * @property {string}  subtitle    Secondary label.
 * @property {?string} assigneeId  User the work is assigned to, or null.
 * @property {?string} dueDate     ISO date string, or null when undated.
 * @property {?string} status      Source status, when meaningful.
 * @property {number}  count       How many underlying items (default 1).
 * @property {'admin-app'} audience Always admin-app here (never employee-portal).
 * @property {Object}  nav         Navigation descriptor consumed by the UI.
 * @property {Object}  raw         The originating entity (for callers needing more).
 */

// A question is "open" (owes an answer) when no user has scored it yet.
function isQuestionOpen(answers) {
  const values = Object.values(answers || {});
  if (values.length === 0) return true;
  return values.every((a) => a == null || a.metric === null || a.metric === undefined);
}

// Re-implements GapAnalysisDetail.resolveAssignee without importing the component.
// Priority: question-level override (q.assigneeId) → section-level assignment.
function resolveGapAssignee(ga, sec, q) {
  if (q?.assigneeId) return q.assigneeId;
  const assignments = ga?.assignments || [];
  if (!assignments.length) return null;
  const candidates = [
    q?.section,
    sec?.domainCode,
    sec?.id,
    q?.section ? `scf::${q.section}` : null,
    sec?.domainCode ? `scf::${sec.domainCode}` : null,
  ].filter(Boolean);
  for (const key of candidates) {
    const hit = assignments.find((a) => a.sectionKey === key);
    if (hit) return hit.userId;
  }
  return null;
}

const GAP_TERMINAL_STATUSES = new Set(['complete', 'reported', 'archived']);

/**
 * Per-(assessment, assignee) gap-question assignments.
 *
 * Exported standalone (in addition to being a contributor) so the Action Centre
 * badge and modal derive the exact same list as the calendar — one predicate,
 * three surfaces, no drift. When `userId` is provided the result is filtered to
 * that user; otherwise every assignee's batch is returned.
 *
 * @param {Object} state
 * @param {?string} [userId]
 * @returns {Assignment[]}
 */
function listGapQuestionAssignments(state, userId = null) {
  const out = [];
  for (const ga of state?.gapAnalyses || []) {
    if (!ga || ga.archivedAt) continue;
    if (GAP_TERMINAL_STATUSES.has(ga.status)) continue;
    // Tally open questions per resolved assignee for this assessment.
    const perUser = new Map();
    for (const sec of ga.sections || []) {
      for (const q of sec.questions || []) {
        if (!isQuestionOpen(q.answers)) continue;
        const assignee = resolveGapAssignee(ga, sec, q);
        if (!assignee) continue;
        perUser.set(assignee, (perUser.get(assignee) || 0) + 1);
      }
    }
    for (const [assigneeId, count] of perUser) {
      if (count === 0) continue;
      if (userId && assigneeId !== userId) continue;
      out.push({
        id: `gap-${ga.id}-${assigneeId}`,
        kind: 'gap-questions',
        module: 'audit',
        title: ga.name || 'Gap assessment',
        subtitle: `${count} gap question${count === 1 ? '' : 's'} to complete`,
        assigneeId,
        dueDate: ga.dueDate || null,
        status: ga.status || null,
        count,
        audience: 'admin-app',
        nav: { open: 'gap', gapId: ga.id },
        raw: ga,
      });
    }
  }
  return out;
}

// ─── Contributors ────────────────────────────────────────────────────────────
// Each contributor is a pure function `(state, ctx) => Assignment[]`.
// `ctx` carries derived inputs the selector cannot read from `state` alone:
//   { now: Date, obligationTasks: Task[] }

function contributeDocumentReviews(state /* , ctx */) {
  return (state.documents || []).reduce((acc, doc) => {
    if (!doc?.nextReview) return acc;
    acc.push({
      id: `doc-${doc.id}`,
      kind: 'document-review',
      module: 'document',
      title: doc.title || 'Untitled Document',
      subtitle: `v${doc.version || '1.0'} · ${doc.category || 'Document'}`,
      assigneeId: doc.approverId || doc.owner || null,
      dueDate: doc.nextReview,
      status: doc.documentStatus || null,
      count: 1,
      audience: 'admin-app',
      nav: { open: 'modal', modal: { type: 'document-detail', data: doc } },
      raw: doc,
    });
    return acc;
  }, []);
}

// ─── The one task list ───────────────────────────────────────────────────────
// A Tier 1/2/3 task is finished by `completed` / `closed`, and a Tier 2 action
// can also be `cancelled` (with a reason — D-2 A on GH #7167). A cancelled
// action is neither open nor done: it is never owed work, and it never counts
// as the task backing anything.
const TERMINAL_TASK_STATUSES = new Set(['closed', 'completed', 'cancelled']);

/** True while a task, obligation task or domain action is still owed work. */
function isOpenTaskStatus(status) {
  return !TERMINAL_TASK_STATUSES.has(status);
}

/** The one assignee match, shared by the Action Centre list and its badge. */
function isAssignedTo(task, userId) {
  if (!task || !userId) return false;
  return (task.assigneeId || task.ownerId) === userId;
}

// ─── Owed effectiveness checks (OPP004A-04, GH #7167) ────────────────────────
// A completed MR action is not done with until its effectiveness is checked
// (TRYGGR_INTENT.md §12 "Monitoring is not an event", MGMTREVIEW-B-003). The
// check is owed work — derived, never stored — for the named verifier:
// the review's chair, else whoever created the review, else the action's
// owner. It is due VERIFICATION_DUE_DAYS after the completion; a not-yet or
// not-effective verdict owes a follow-up action on the same review
// (`followUpOf`), due the same number of days after the verdict, until a
// follow-up that is not cancelled exists. A legacy close with no readable date
// cannot have a verification placed after it and is left to the OPP004A-05
// migration, which dates it.
const VERIFICATION_DUE_DAYS = 30;

function addDays(iso, days) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return new Date(t + days * 86400000).toISOString().slice(0, 10);
}

function owedMrVerifications(mr, artefactIds) {
  const actions = (mr?.actions || []).map(normaliseMrAction).filter(Boolean);
  const followedUp = new Set(
    actions.filter((a) => a.followUpOf && a.status !== 'cancelled').map((a) => a.followUpOf)
  );
  const verifier = mr?.chair || mr?.createdBy || '';
  const out = [];
  for (const a of actions) {
    if (a.status !== 'completed' || Number.isNaN(Date.parse(a.completedAt))) continue;
    const state = deriveVerificationState(a, artefactIds);
    const name = a.title || 'action';
    let item = null;
    if (state === 'awaiting-verification' || state === 'not-verified-legacy') {
      item = {
        owedKind: 'verify',
        title: `Verify effectiveness: ${name}`,
        dueDate: addDays(a.completedAt, VERIFICATION_DUE_DAYS),
      };
    } else if ((state === 'not-yet' || state === 'not-effective') && !followedUp.has(a.id)) {
      const latest = (a.verificationLog || [])[a.verificationLog.length - 1];
      item = {
        owedKind: 'follow-up',
        title: `${state === 'not-yet' ? 'Raise a re-check' : 'Raise a corrective action'}: ${name}`,
        dueDate: addDays(latest?.at, VERIFICATION_DUE_DAYS),
      };
    }
    if (!item) continue;
    out.push({
      ...item,
      id: `mrv-${a.id}`,
      actionId: a.id,
      verificationState: state,
      status: 'pending',
      assigneeId: verifier || a.assigneeId || null,
      _source: 'management-review-verification',
      managementReviewId: mr.id,
      managementReviewTitle: mr.title,
      managementReview: mr,
    });
  }
  return out;
}

/**
 * The viewer's question tasks (ACEA-05) from their comm-questions views: only
 * the rows where they are the assignee, carrying them as assignee. One
 * definition, read by the Action Centre, the Tasks page and the calendar, so
 * the three cannot disagree about which questions are the viewer's work
 * (GH #7583). The server returns only the caller's own rows (COMM-B-011).
 *
 * @param {Object[]} commQuestions  useMyCommQuestions / comm-questions ?action=list
 * @param {string} userId           the viewer
 * @returns {Object[]}
 */
function commQuestionTaskViews(commQuestions, userId) {
  return (commQuestions || [])
    .filter((q) => q && q.role === 'assignee')
    .map((q) => ({ ...q, assigneeId: userId }));
}

/**
 * Every task source, flattened and tagged with `_source` — obligation tasks,
 * ad-hoc tasks, and the Tier 2 domain actions of risks, evidence requests and
 * management reviews. The calendar (via contributeTasks), the Action Centre and
 * its badge all read this one list, so an item is counted once, the same way,
 * everywhere (TASKS-B-001, ADR-0003).
 *
 * Management-review actions are read through normaliseMrAction, so a legacy
 * `{ owner, status: 'open'|'closed' }` row reads as the Tier 2 shape
 * (OPP004A-03, GH #7167) until OPP004A-06 retires the legacy branch.
 *
 * Workflow steps (PDWA-04, docs/workflow-engine-design.md §3b) are Tier 2
 * domain actions whose domain record is the workflow run: the open human step
 * instances the server returns (workflow-runs-read ?view=open-steps) are passed
 * in exactly the way obligation tasks are, carrying the DomainAction shape and
 * enum unchanged — one source among the others, never a list of their own
 * (WF-B-002). The step instance is the only place their status lives.
 *
 * Question tasks (ACEA-05, rev 4 §5.2) are Tier 2 domain actions whose domain
 * record is a comm_questions row: the open rows the server returns for the
 * viewer as assignee (comm-questions ?action=list) are passed in the same way,
 * keeping the Tier 2 enum unchanged (TASKS-B-001). The server returns only the
 * caller's own, so this source is only ever the viewer's.
 *
 * @param {Object} state
 * @param {Object[]} [obligationTasks]
 * @param {Object[]} [workflowSteps]
 * @param {Object[]} [commQuestionTasks]  assignee views from comm-questions
 * Reviews of halted recurring workflows (PDW-HALT-2,
 * docs/workflow-halt-review-design.md §2) are Tier 2 domain actions whose
 * domain record is the workflow definition: the open rows the server returns
 * (workflow-runs-read ?view=halt-reviews) are passed in like workflow steps,
 * status enum unchanged, one item per review. Every OPEN review is owed work,
 * whatever its cause (WF-B-011, slice 4b: a release-held review offers Retire,
 * Accept or Ask for it to be switched back on). A review whose own decision
 * opened a re-enable request is completed, so it is decided, not owed.
 *
 * @param {Object[]} [riskAiTasks]  riskAiAssessmentTasks(...) — undecided AI risk assessments
 * @param {Object[]} [haltReviews]  open halt reviews (useMyHaltReviews / listTenantHaltReviews)
 * @returns {Object[]}
 */
function collectTaskSources(
  state,
  obligationTasks = [],
  workflowSteps = [],
  commQuestionTasks = [],
  riskAiTasks = [],
  haltReviews = []
) {
  if (!state) return [];
  return [
    // "Decide on AI assessment" (RAI-05; design §1.1): an undecided AI risk
    // assessment is owed work — passed in already shaped by
    // riskAiAssessmentTasks, exactly like workflow steps.
    ...(riskAiTasks || []).map((t) => ({ ...t, _source: 'risk-ai-assessment' })),
    ...(obligationTasks || []).map((t) => ({ ...t, _source: 'obligation' })),
    ...(commQuestionTasks || []).map((q) => ({
      ...q,
      _source: 'comm-question',
      title:
        q.kind === 'wrong-answer'
          ? 'Review a reported AI answer'
          : `Answer a question from ${q.askerName || 'a colleague'}`,
      dueDate: q.dueAt || null,
    })),
    ...(workflowSteps || []).map((s) => ({
      ...s,
      _source: 'workflow-step',
      workflowRunId: s.runId,
    })),
    ...haltReviewTasks(haltReviews),
    ...(state.adhocTasks || []).map((t) => ({ ...t, _source: 'adhoc' })),
    ...(state.risks || []).flatMap((r) =>
      (r.actions || []).map((a) => ({
        ...a,
        _source: 'risk-action',
        riskId: r.id,
        riskName: r.name,
      }))
    ),
    // Evidence-request actions are Tier 2 domain-backed work, exactly like risk
    // actions — they reach the calendar and Action Centre through this same
    // list rather than a bucket of their own (ADR-0003).
    ...(state.evidenceRequests || []).flatMap((er) =>
      (er.actions || []).map((a) => ({
        ...a,
        _source: 'evidence-request-action',
        evidenceRequestId: er.id,
        evidenceRequestName: er.name,
      }))
    ),
    ...(state.managementReviews || []).flatMap((mr) =>
      (mr?.actions || []).reduce((acc, raw) => {
        const a = normaliseMrAction(raw);
        if (a) {
          acc.push({
            ...a,
            _source: 'management-review-action',
            managementReviewId: mr.id,
            managementReviewTitle: mr.title,
          });
        }
        return acc;
      }, [])
    ),
    // Owed effectiveness checks — read against the artefacts in the state
    // given, so every surface derives "effective" the same way.
    ...(state.managementReviews || []).flatMap((mr) =>
      owedMrVerifications(mr, artefactIdsOf(state))
    ),
  ];
}

/**
 * The halt reviews that are owed work, as tasks. Facts-only title (design §5,
 * WF-B-005). Only an open review is owed, whatever the caller passed (a
 * tenant read also carries each definition's latest closed review).
 */
function haltReviewTasks(haltReviews) {
  return (haltReviews || [])
    .filter((r) => r && r.reviewId && (r.status === 'pending' || r.status === 'in-progress'))
    .map((r) => ({
      ...r,
      id: r.reviewId,
      _source: 'workflow-halt-review',
      title: `Recurring workflow stopped: ${r.definitionTitle || r.definitionId} — decide what happens to it`,
      dueDate: r.dueDate || null,
      status: r.status,
    }));
}

const PORTAL_ROLES = new Set(['employee-portal', 'candidate-portal']);

/**
 * The "decide on AI assessment" Tier 2 tasks (RAI-05; design §1.1, settled
 * rule: an undecided assessment is owed work, never swept, never auto-decided).
 * Input is risk-ai-assess `list` rows; only those whose derived decision is
 * owed become tasks. The task carries no status of its own beyond "open": it
 * closes only when the assessment is decided (create-risk or dismiss), and the
 * next list read drops it.
 *
 * Who owes it: the person who ran the assessment while they are a current
 * internal user of the tenant; otherwise nobody is named and every admin owes
 * it (the unassigned-to-admins rule workflow steps use), so owed work survives
 * an owner leaving (TRYGGR_INTENT §12). An empty `users` list is "not loaded",
 * never "nobody is current".
 *
 * @param {Array<{id, createdBy, createdAt, description, decision}>} assessments
 * @param {Array<object>} users  tenant users
 * @returns {Object[]}
 */
function riskAiAssessmentTasks(assessments, users = []) {
  const known = Array.isArray(users) && users.length > 0;
  const current = new Set(
    (users || [])
      .filter((u) => u && !PORTAL_ROLES.has(u.role) && u.disabled !== true && u.status !== 'disabled')
      .map((u) => u.id || u.userId)
      .filter(Boolean)
  );
  return (assessments || [])
    .filter((a) => a && a.id && a.decision && a.decision.owed === true)
    .map((a) => {
      const description = typeof a.description === 'string' ? a.description.trim() : '';
      const short = description.length > 80 ? `${description.slice(0, 77)}…` : description;
      return {
        id: `rai-${a.id}`,
        assessmentId: a.id,
        title: `Decide on AI assessment${short ? `: ${short}` : ''}`,
        description: short || null,
        assigneeId: !known || current.has(a.createdBy) ? a.createdBy || null : null,
        createdBy: a.createdBy || null,
        createdAt: a.createdAt || null,
        status: 'open',
        dueDate: null,
        pendingRiskId: a.decision.pendingRiskId || null,
      };
    });
}

function artefactIdsOf(state) {
  return Array.isArray(state?.artefacts)
    ? new Set(state.artefacts.map((a) => a?.id).filter(Boolean))
    : undefined;
}

/**
 * The open tasks a user owes, from the one list. The Action Centre renders this
 * and its badge counts it, so the two cannot disagree.
 */
function listOpenTasksForUser(
  state,
  userId,
  obligationTasks = [],
  commQuestionTasks = [],
  workflowSteps = [],
  riskAiTasks = [],
  haltReviews = []
) {
  return collectTaskSources(state, obligationTasks, workflowSteps, commQuestionTasks, riskAiTasks, haltReviews).filter(
    (t) => isAssignedTo(t, userId) && isOpenTaskStatus(t.status)
  );
}

const DOMAIN_ACTION_SOURCES = new Set([
  'risk-action',
  'evidence-request-action',
  'management-review-action',
]);

function taskSubtitle(task) {
  switch (task._source) {
    case 'risk-action':
      return `Risk Action · ${task.riskName || task.riskId}`;
    case 'evidence-request-action':
      return `Evidence · ${task.evidenceRequestName || task.evidenceRequestId}`;
    case 'management-review-action':
      return `Management Review · ${task.managementReviewTitle || task.managementReviewId}`;
    case 'management-review-verification':
      return `Effectiveness check · ${task.managementReviewTitle || task.managementReviewId}`;
    case 'workflow-step':
      return `Workflow · ${task.workflowTitle || task.definitionId || task.workflowRunId}`;
    case 'workflow-halt-review':
      return `Workflow review · ${task.definitionTitle || task.definitionId}`;
    case 'risk-ai-assessment':
      return 'Risk · AI assessment awaiting your decision';
    case 'comm-question':
      return task.kind === 'wrong-answer' ? 'Question · reported AI answer' : 'Question · awaiting your answer';
    case 'obligation':
      return `Obligation · ${task.status}`;
    default:
      return `Task · ${task.status}`;
  }
}

function contributeTasks(state, ctx) {
  return collectTaskSources(
    state,
    ctx.obligationTasks,
    ctx.workflowSteps,
    ctx.commQuestionTasks,
    ctx.riskAiTasks,
    ctx.haltReviews
  ).reduce((acc, task) => {
    if (!isOpenTaskStatus(task.status)) return acc;
    // An undecided AI assessment is decided in its own modal (create a draft
    // risk, or dismiss with a reason) — never through a generic task status.
    if (task._source === 'risk-ai-assessment') {
      acc.push({
        id: `task-${task.id}`,
        kind: 'task',
        module: 'risk',
        title: task.title,
        subtitle: taskSubtitle(task),
        assigneeId: task.assigneeId || null,
        dueDate: null,
        status: task.status,
        count: 1,
        audience: 'admin-app',
        nav: { open: 'modal', modal: { type: 'risk-ai-assess', data: { assessmentId: task.assessmentId } } },
        raw: task,
      });
      return acc;
    }
    const isDomainAction = DOMAIN_ACTION_SOURCES.has(task._source);
    // A workflow step opens its own command surface (PDWB-02, design §3b.3):
    // the step's task modal completes it through workflow-steps-write. It
    // never falls through to the domain-action modal, which has no button for it.
    if (task._source === 'workflow-step') {
      acc.push({
        id: `task-${task.id}`,
        kind: 'task',
        module: 'task',
        title: task.title || 'Workflow step',
        subtitle: taskSubtitle(task),
        assigneeId: task.assigneeId || null,
        dueDate: task.dueDate || null,
        status: task.status,
        count: 1,
        audience: 'admin-app',
        nav: { open: 'modal', modal: { type: 'workflow-step', data: task } },
        raw: task,
      });
      return acc;
    }
    // A halted workflow's review is decided in its own modal (Retire / Accept /
    // Re-enable with a reason, through workflow-halt-reviews-write) — never through a
    // generic task status (WF-B-011).
    if (task._source === 'workflow-halt-review') {
      acc.push({
        id: `task-${task.id}`,
        kind: 'task',
        module: 'task',
        title: task.title,
        subtitle: taskSubtitle(task),
        assigneeId: task.assigneeId || null,
        dueDate: task.dueDate || null,
        status: task.status,
        count: 1,
        audience: 'admin-app',
        nav: { open: 'modal', modal: { type: 'workflow-halt-review', data: task } },
        raw: task,
      });
      return acc;
    }
    // A question task is answered in its own modal, the one the Action Centre
    // opens (ACEA-05) — never through a generic task status.
    if (task._source === 'comm-question') {
      acc.push({
        id: `task-${task.id}`,
        kind: 'task',
        module: 'task',
        title: task.title,
        subtitle: taskSubtitle(task),
        assigneeId: task.assigneeId || null,
        dueDate: task.dueDate || null,
        status: task.status,
        count: 1,
        audience: 'admin-app',
        nav: { open: 'modal', modal: { type: 'comm-question', data: task } },
        raw: task,
      });
      return acc;
    }
    if (task._source === 'management-review-verification') {
      acc.push({
        id: `task-${task.id}`,
        kind: 'task',
        module: 'task',
        title: task.title,
        subtitle: taskSubtitle(task),
        assigneeId: task.assigneeId || null,
        dueDate: task.dueDate || null,
        status: task.status,
        count: 1,
        audience: 'admin-app',
        nav: { open: 'modal', modal: { type: 'mr-modal', data: task.managementReview } },
        raw: task,
      });
      return acc;
    }
    const navType =
      task._source === 'obligation'
        ? 'obligation-task-detail'
        : isDomainAction
          ? 'domain-action-detail'
          : 'adhoc-task-detail';
    acc.push({
      id: `task-${task.id || task.obligationId}`,
      kind: isDomainAction ? 'risk-action' : 'task',
      module: task.riskId ? 'risk' : task._source === 'evidence-request-action' ? 'audit' : 'task',
      title: task.title || 'Untitled Task',
      subtitle: taskSubtitle(task),
      assigneeId: task.assigneeId || null,
      dueDate: task.dueDate || null,
      status: task.status || null,
      count: 1,
      audience: 'admin-app',
      nav: { open: 'modal', modal: { type: navType, data: task } },
      raw: task,
    });
    return acc;
  }, []);
}

function contributeRiskReassessments(state, ctx) {
  const now = ctx.now instanceof Date ? ctx.now : new Date(ctx.now || Date.now());
  return (state.risks || []).reduce((acc, risk) => {
    if (!['open', 'in-analysis'].includes(risk.status)) return acc;
    // A cancelled action backs nothing (D-2 A): only owed, dated work counts.
    const hasLinkedTask = (risk.actions || []).some((a) => isOpenTaskStatus(a.status) && a.dueDate);
    if (hasLinkedTask) return acc;
    const baseStr = risk.updatedAt || risk.createdAt;
    if (!baseStr) return acc;
    const base = new Date(baseStr);
    if (isNaN(base)) return acc;
    const date = new Date(base);
    date.setDate(date.getDate() + 90);
    // Drop synthetic dates that fall more than a month before "now" — they are
    // too stale to be useful and would otherwise clutter the first column.
    if (date < new Date(now.getFullYear(), now.getMonth() - 1, 1)) return acc;
    acc.push({
      id: `risk-${risk.id}`,
      kind: 'risk-reassessment',
      module: 'risk',
      title: risk.name || 'Unnamed Risk',
      subtitle: `Reassessment due · ${risk.status}`,
      assigneeId: risk.reviewerId || risk.approverId || null,
      dueDate: date.toISOString(),
      status: risk.status || null,
      count: 1,
      audience: 'admin-app',
      nav: { open: 'modal', modal: { type: 'risk-detail', data: risk } },
      raw: risk,
    });
    return acc;
  }, []);
}

function contributeGapQuestions(state /* , ctx */) {
  return listGapQuestionAssignments(state, null);
}

/**
 * The contributor registry. Adding an assignment-bearing entity means adding a
 * contributor here (and registering its `sources` so the coverage gate passes).
 *
 * `sources` lists the top-level `state` keys the contributor reads as its
 * primary assignment store — used by `scripts/check-assignment-coverage.js`.
 */
const ASSIGNMENT_CONTRIBUTORS = [
  { id: 'document-review', sources: ['documents'], fn: contributeDocumentReviews },
  {
    id: 'tasks',
    sources: ['adhocTasks', 'evidenceRequests', 'managementReviews'],
    fn: contributeTasks,
  },
  { id: 'risk-reassessment', sources: ['risks'], fn: contributeRiskReassessments },
  { id: 'gap-questions', sources: ['gapAnalyses'], fn: contributeGapQuestions },
];

/**
 * Top-level `state` collections that carry dated, user-assigned work. The
 * coverage gate (`scripts/check-assignment-coverage.js`) requires every entry
 * here to be either covered by a contributor's `sources` above, or explicitly
 * allow-listed in `docs/assignment-coverage-gaps.md` with a reason. This is the
 * tripwire that stops a new assignable entity from silently never appearing on
 * a deadline surface.
 */
const ASSIGNMENT_BEARING_COLLECTIONS = [
  'documents',
  'adhocTasks',
  'risks',
  'gapAnalyses',
  'findings',
  'evidenceRequests',
  'biaAssessments',
  'managementReviews',
  'uarCampaigns',
];

/**
 * Build the full, normalised assignment list from tenant state.
 *
 * @param {Object} state
 * @param {{ now?: Date, obligationTasks?: Object[], workflowSteps?: Object[] }} [ctx]
 *   workflowSteps — the viewer's open workflow steps (useMyWorkflowSteps), PDWB-02
 *   riskAiTasks — riskAiAssessmentTasks(...) of the tenant's undecided AI assessments, RAI-05
 *   commQuestionTasks — commQuestionTaskViews(...) of the viewer's questions, ACEA-05
 *   haltReviews — open reviews of halted recurring workflows (useMyHaltReviews), PDW-HALT-2
 * @returns {Assignment[]}
 */
function getAllAssignments(state, ctx = {}) {
  if (!state) return [];
  const resolvedCtx = {
    now: ctx.now || new Date(),
    obligationTasks: ctx.obligationTasks || [],
    workflowSteps: ctx.workflowSteps || [],
    riskAiTasks: ctx.riskAiTasks || [],
    commQuestionTasks: ctx.commQuestionTasks || [],
    haltReviews: ctx.haltReviews || [],
  };
  const out = [];
  for (const contributor of ASSIGNMENT_CONTRIBUTORS) {
    const items = contributor.fn(state, resolvedCtx) || [];
    for (const item of items) out.push(item);
  }
  return out;
}

module.exports = {
  listGapQuestionAssignments,
  isOpenTaskStatus,
  isAssignedTo,
  VERIFICATION_DUE_DAYS,
  collectTaskSources,
  commQuestionTaskViews,
  riskAiAssessmentTasks,
  listOpenTasksForUser,
  ASSIGNMENT_CONTRIBUTORS,
  ASSIGNMENT_BEARING_COLLECTIONS,
  getAllAssignments,
};
